import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";

import { useSelector } from "react-redux";
import { selectUser } from "../../core/auth/authSlice";
import { addMessage } from "../../core/auth/chatSlice";
import { addSuccessMessage } from "../../core/errors/errorSlice";
import { PostApiRoutes } from "../../shared/api-routes/post/post.api-routes";
import { User } from "../../shared/models";
import http from "../../shared/utils/http";
import { useLocalizedFormatters } from "../../shared/hooks/useLocalizedFormatters";
import { resolveMediaUrl } from "../../shared/utils/mediaUtils";
import ClickIcon from "../essentials/icon-buttons/click-icon/ClickIcon";
import ClickIconMenu from "../essentials/icon-buttons/click-icon/ClickIconMenu";
import ToggleIcon from "../essentials/icon-buttons/toggle-icon/ToggleIcon";
import { getDescriptionHtml } from "../essentials/input/text-area/RichTextInput";
import { AcedMenuOption } from "../essentials/menu/AcedMenu";
import AcedModal from "../essentials/modals/aced-modal/AcedModal";
import ConfirmationModal from "../essentials/modals/confirmation-modal/ConfirmationModal";
import UserCard, { UserPreviewInfo } from "../essentials/user-card/UserCard";
import CommentModal from "./components/comment-modal/comment-modal/CommentModal";
import EditModal from "./components/edit-modal/EditModal";
// import LikeModal from "./components/like-modal/LikeModal"; // Disabled - GET_LIKES endpoint no longer exists
import { useMediaProcessing } from "../../shared/hooks/useMediaProcessing";
import { SendButton } from "./components/SendBtn/SendButton";
import Sharer from "./components/sharer/Sharer";
import VideoPlayer from "./components/video-player/VideoPlayer";
import "./Post.scss";

export interface PostInfo {
	id: string;
	author: UserPreviewInfo;
	pic: string;
	name: string;
	sport: string;
	title: string;
	description: any;
	image?: string;
	time: string;
	likes: any[];
	comments: number;
	createdAt: string;
	isLiked: boolean;
	likeCount: number;
	commentCount: number;
	hasImage: boolean;
	repostedBy: { id: string; name: string; surname: string };

	// Direct image URLs (replaces blob-proxy fetching)
	imageUrl?: string;
	thumbUrl?: string;

	// New unified media fields (replaces imageStatus, videoStatus, videoProgress)
	type?: "image" | "video" | "text";
	mediaStatus?: "processing" | "ready" | "failed";
	postProgress?: number; // 0-100

	// Video-specific fields
	videoUrl?: string;
	posterUrl?: string;
	previewUrl?: string;
	width?: number;
	height?: number;
}

export type PostProps = {
	postInfo: PostInfo;
	children?: any;
	onDeletePost: any;
};

const Post: React.FC<PostProps> = ({ postInfo, children, onDeletePost }) => {
	const [liked, setLiked] = useState(postInfo.isLiked);
	const [likes, setLikes] = useState<number>(postInfo.likeCount);
	const [commentCount, setCommentCount] = useState<number>(
		postInfo.commentCount,
	);
	const [isExpanded, setIsExpanded] = useState(false);

	// Use media processing hook to poll for mediaStatus updates
	const { currentPost } = useMediaProcessing(postInfo);

	// const [isLikeModalOpen, setLikeModalOpen] = useState(false); // Disabled - GET_LIKES endpoint no longer exists
	const [isCommentModalOpen, setCommentModalOpen] = useState(false);
	const [isEditModalOpen, setEditModalOpen] = useState<boolean>(false);
	const [isDeleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

	const { actorId } = useSelector(selectUser) as User;
	const { formatRelativeTime } = useLocalizedFormatters();
	const { t } = useTranslation();

	const appDispatch = useAppDispatch();

	const likeUnlike = () => {
		setLiked(!liked);
		if (!liked) {
			http.post(PostApiRoutes.LIKE_POST(postInfo.id)).catch((err) => {
				if (err.response.status !== 409) {
					throw err;
				}
			});
			setLikes(likes + 1);
		}
		if (liked) {
			http.delete(PostApiRoutes.REMOVE_LIKE(postInfo.id)).catch((err) => {
				if (err.response.status !== 404) {
					throw err;
				}
			});
			setLikes(likes - 1);
		}
	};

	const copyLink = () => {
		navigator.clipboard.writeText(window.location.origin + "/p/" + postInfo.id);
		appDispatch(
			addSuccessMessage({ title: t("common.messages.copiedToClipboard") }),
		);
	};

	const menuOptions = () => {
		const alwaysOptions = [
			{ text: t("common.buttons.copyLink"), action: copyLink },
			{ text: t("common.buttons.embed") },
		];

		const meOptions = [
			{
				text: t("common.buttons.edit"),
				action: () => {
					setEditModalOpen(true);
				},
			},
			{
				text: t("common.buttons.delete"),
				color: "red",
				action: () => {
					setDeleteModalOpen(true);
				},
			},
		];

		const stopFollowingAction = { text: t("common.buttons.stopFollowing") };
		const reportAction = { text: t("common.buttons.report") };

		const youOptions: AcedMenuOption[] = [];

		return actorId === postInfo.author.id
			? [...meOptions, ...alwaysOptions]
			: [...alwaysOptions, ...youOptions];
	};

	const onEdit = (newDescription: string) => {
		http
			.patch(PostApiRoutes.UPDATE_POST(postInfo.id), {
				description: newDescription,
			})
			.then((res) => {
				postInfo.description = res.description;
				setEditModalOpen(false);
			});
	};

	const getDescriptionText = (desc: any): string => {
		try {
			// If it's already a string, return it
			if (typeof desc === "string") {
				// Try to parse it as JSON
				try {
					const parsed = JSON.parse(desc);
					// If it's a Quill Delta format, extract text
					if (parsed && parsed.ops && Array.isArray(parsed.ops)) {
						return parsed.ops.map((op: any) => op.insert || "").join("");
					}
					// If JSON.parse succeeded but it's just a plain string
					return parsed;
				} catch (e) {
					// If JSON.parse failed, it's plain text
					return desc;
				}
			}
			// If it's already an object (Quill Delta format)
			if (desc && desc.ops && Array.isArray(desc.ops)) {
				return desc.ops.map((op: any) => op.insert || "").join("");
			}
			return "";
		} catch (e) {
			return "";
		}
	};

	const shouldTruncate = () => {
		const plainText = getDescriptionText(postInfo.description);
		return plainText.length > 100;
	};

	const getTruncatedDescription = (desc: any) => {
		if (isExpanded || !shouldTruncate()) {
			return getDescriptionHtml(desc || "");
		}

		const plainText = getDescriptionText(desc);
		const truncated = plainText.substring(0, 100);
		return truncated;
	};

	const deleteAction = () => {
		setDeleteModalOpen(false);
		http.delete(PostApiRoutes.DELETE_POST(postInfo.id)).then((res) => {
			onDeletePost(res.id);
		});
	};

	// Effect for updating likes/comments
	useEffect(() => {
		setLikes(postInfo.likeCount);
		setLiked(postInfo.isLiked);
		setCommentCount(postInfo.commentCount);
	}, [postInfo.id, postInfo.likeCount, postInfo.isLiked, postInfo.commentCount]);

	return (
		<div>
			{postInfo.repostedBy && <Sharer sharer={postInfo.repostedBy} />}
			<div className="post">
				<div className="post-header">
					<div className="post-header-left">
						<UserCard
							action={"follow"}
							userInfo={postInfo.author}
							className="flex-1"
						/>
						<div className="post-date">
							{formatRelativeTime(postInfo.createdAt)}
						</div>
					</div>

					<ClickIconMenu
						icon="dots"
						tooltip={t("common.tooltips.moreOptions")}
						options={menuOptions()}
					/>
				</div>

				<div className="divider" />

				<div className="content">
					{isExpanded || !shouldTruncate() ? (
						<div
							className="description"
							dangerouslySetInnerHTML={{
								__html: getDescriptionHtml(postInfo.description || ""),
							}}
						/>
					) : (
						<div className="description">
							{getTruncatedDescription(postInfo.description)}...
						</div>
					)}
					{shouldTruncate() && (
						<div
							className="read-more"
							onClick={() => setIsExpanded(!isExpanded)}
						>
							{isExpanded
								? t("common.buttons.readLess")
								: t("common.buttons.readMore")}
						</div>
					)}
					{/* Video post rendering */}
					{currentPost.type === "video" && (
						<VideoPlayer
							videoUrl={currentPost.videoUrl}
							posterUrl={currentPost.posterUrl}
							previewUrl={currentPost.previewUrl}
							width={currentPost.width}
							height={currentPost.height}
							mediaStatus={currentPost.mediaStatus}
						/>
					)}

					{/* Image post rendering — use direct CDN/public URL, no blob proxy */}
					{currentPost.type === "image" && (currentPost.thumbUrl || currentPost.imageUrl) && (
						<div className="img-container">
							<img
								className="img"
								src={resolveMediaUrl(currentPost.thumbUrl || currentPost.imageUrl)}
								alt=""
							/>
						</div>
					)}
				</div>

				<div className="post__footer">
					<div className="group">
						<div className="element">
							<ToggleIcon
								icon="like"
								isToggled={liked}
								tooltip={t("common.tooltips.like")}
								onClick={likeUnlike}
							/>
							<div className="label">{likes}</div>
						</div>
						<div className="element">
							<ClickIcon
								onClick={() => {
									setCommentModalOpen(true);
								}}
								icon="comment"
								tooltip={t("common.tooltips.comment")}
								label={commentCount}
							/>
							{/* <div onClick={() => {}} className="label button">
                0
              </div> */}
						</div>
						{/* <div className="element">
                <Comment
                  // onClick={}
                  className={`icon button`}
                />
                <div className="label button">{postInfo.comments}</div>
              </div> */}
					</div>
					<div className="group">
						<SendButton
							title={t("post.sendPost")}
							onSend={async (chatIds: string[]) => {
								const messages = await http.post(
									PostApiRoutes.SHARE_POST(postInfo.id),
									{
										chatIds,
									},
								);
								chatIds.forEach((chatId, index) => {
									appDispatch(
										addMessage({
											chatId,
											message: messages[index],
										}),
									);
								});
							}}
						/>
					</div>
				</div>
				{/* Likes modal disabled - GET_LIKES endpoint no longer exists */}
				{/* <AcedModal
					title="Likes"
					state={{
						isModalOpen: isLikeModalOpen,
						setModalOpen: setLikeModalOpen,
					}}
				>
					<LikeModal post_id={postInfo.id} />
				</AcedModal> */}
				<AcedModal
					title={t("post.comments")}
					state={{
						isModalOpen: isCommentModalOpen,
						setModalOpen: setCommentModalOpen,
					}}
				>
					<CommentModal
						state={{ commentCount, setCommentCount }}
						post_id={postInfo.id}
					/>
				</AcedModal>
				{isEditModalOpen && (
					<EditModal
						title={t("post.editPost")}
						state={{
							isModalOpen: isEditModalOpen,
							setModalOpen: setEditModalOpen,
						}}
						description={getDescriptionText(postInfo.description)}
						onEdit={onEdit}
					/>
				)}

				{isDeleteModalOpen && (
					<ConfirmationModal
						title={t("post.deletePost")}
						state={{
							isModalOpen: isDeleteModalOpen,
							setModalOpen: setDeleteModalOpen,
						}}
						onConfirm={deleteAction}
					/>
				)}
			</div>
		</div>
	);
};

export default Post;
